import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import JSZip from 'jszip';
import '../styles/SelectedResults.css';
import Swal from 'sweetalert2';

const SelectedResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [items, setItems] = useState([]);
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [activePhotoIdx, setActivePhotoIdx] = useState(null);
  const [shapeAssignments, setShapeAssignments] = useState([]); // array of selectedPhotos indices per shape
  const [videoAssignments, setVideoAssignments] = useState({}); // { shapeIdx: videoFilename } for video assignments
  const [shapeOffsets, setShapeOffsets] = useState([]); // horizontal pan offsets per shape
  const [shapeVerticalOffsets, setShapeVerticalOffsets] = useState([]); // vertical pan offsets per shape
  const [panState, setPanState] = useState(null); // { shapeIdx, startClientX, startClientY, startOffset, startVerticalOffset }
  const [photoDims, setPhotoDims] = useState({}); // { filename: { w, h } }
  const [shapeScales, setShapeScales] = useState([]); // zoom scale per shape (>= 1)
  const [displayMode, setDisplayMode] = useState('photo'); // 'photo' or 'video'
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const canvasRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState('normal'); // filter: normal, bnw, sepia, etc
  const filterOptions = [
    { key: 'normal', label: 'Normal', icon: '🌈' },
    { key: 'bnw', label: 'B&W', icon: '⚫️' },
    { key: 'sepia', label: 'Sepia', icon: '🟤' },
    { key: 'cool', label: 'Cool', icon: '💙' },
    { key: 'warm', label: 'Warm', icon: '🧡' },
  ];
  const [pinchState, setPinchState] = useState(null);
  const [activeShapeId, setActiveShapeId] = useState(null);
  const [editingShapeId, setEditingShapeId] = useState(null); // shape yang sedang diedit di preview
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);

  // Move shapeItems and allItems here so all hooks can use them
  const shapeItems = items.filter(it => it.item_type === 'shape');
  const allItems = items.slice();

  // --- Countdown Timer State ---
  const [countdown, setCountdown] = useState(180); // 2 minutes
  const countdownIntervalRef = useRef(null);
  // Start countdown on mount
  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownIntervalRef.current);
  }, []);

  // Format countdown mm:ss
  const formatCountdown = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  // Auto-assign and set email when countdown ends
  useEffect(() => {
    if (countdown === 0 && shapeAssignments.some(a => a === null) && selectedPhotos.length >= shapeItems.length) {
      // Randomly assign photos to shapes (no duplicate)
      const shuffled = [...selectedPhotos].sort(() => Math.random() - 0.5);
      const assignments = shuffled.slice(0, shapeItems.length).map((_, i) => i);
      setShapeAssignments(assignments);
      setEmailInput('laviopaperie@gmail.com');
    }
  }, [countdown, shapeAssignments, selectedPhotos, shapeItems.length]);

  // After emailInput is set and countdown is 0, auto-click Done
  useEffect(() => {
    if (countdown === 0 && emailInput === 'laviopaperie@gmail.com') {
      setTimeout(() => {
        captureFrameAsPNG();
      }, 500);
    }
  }, [countdown, emailInput]);

  const API_BASE = 'https://api.laviopaperie.com';

  useEffect(() => {
    // Ambil data dari state navigation jika ada
    if (location.state && location.state.selectedPhotos) {
      setSelectedPhotos(location.state.selectedPhotos);
    }

    fetchSessionData();
  }, [sessionId, location.state]);

  const fetchSessionData = async () => {
    try {
      const response = await fetch(`https://api.laviopaperie.com/api/photo-session/${sessionId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch session data');
      }
      const data = await response.json();
      setSessionData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load email from sessionData when available
  useEffect(() => {
    if (sessionData && sessionData.email) {
      setEmailInput(sessionData.email);
    }
  }, [sessionData]);

  // Load selected frame (workspace) and items
  useEffect(() => {
    const loadFrame = async () => {
      try {
        // Get frame ID dari sessionData.grid (hasil GET photo-session)
        const frameId = Number(sessionData?.grid) || null;
        if (!frameId) return;
        const res = await fetch(`${API_BASE}/api/workspaces/${frameId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Gagal memuat frame');
        setWorkspace(data.workspace);
        setItems(data.items || []);
      } catch (e) {
        // ignore error on frame load
      }
    };
    loadFrame();
  }, [sessionData]);

  // Function to get video filename based on sessionId and photo number
  const getVideoFilename = (sessionId, photoNumber) => {
    return `${sessionId}_video_${photoNumber}.webm`;
  };

  // Function to get video URL
  const getVideoUrl = (sessionId, photoNumber) => {
    const videoFilename = getVideoFilename(sessionId, photoNumber);
    return `${API_BASE}/uploads/${videoFilename}`;
  };

  // Function to extract photo number from filename
  const getPhotoNumberFromFilename = (filename) => {
    // Extract number from filename like "session_123_3.jpg" -> 3
    const match = filename.match(/_(\d+)\.(jpg|jpeg|png|webm)$/);
    return match ? parseInt(match[1], 10) : null;
  };

  // Scale the frame to fit height first, then adjust width if needed
  useEffect(() => {
    const onResize = () => {
      if (!wrapperRef.current || !workspace) return;
      const w = wrapperRef.current.clientWidth || 1;
      const h = wrapperRef.current.clientHeight || 1;
      const cw = Math.max(1, workspace.canvas_width || 1);
      const ch = Math.max(1, workspace.canvas_height || 1);

      // Scale to fit height first, then check if width fits
      const scaleByHeight = h / ch;
      const scaleByWidth = w / cw;

      // Use height-based scaling to ensure frame fills the height
      let s = scaleByHeight;

      // If width becomes too large, scale down proportionally
      if (cw * s > w) {
        s = scaleByWidth;
      }

      setScale(s);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [workspace]);

  // Initialize/reset assignments when shapes change (but not when selected photos change)
  useEffect(() => {
    setShapeAssignments(Array(shapeItems.length).fill(null));
    setShapeOffsets(Array(shapeItems.length).fill(0));
    setShapeVerticalOffsets(Array(shapeItems.length).fill(0));
    setShapeScales(Array(shapeItems.length).fill(1));
    setActivePhotoIdx(null);
    setActiveShapeId(null);
    setEditingShapeId(null);
  }, [shapeItems.length]); // Removed selectedPhotos dependency

  // Load natural sizes for selected photos to compute pan bounds
  useEffect(() => {
    let cancelled = false;
    const loadDims = async () => {
      const entries = await Promise.all(selectedPhotos.map((fn) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve([fn, { w: img.naturalWidth || img.width || 1, h: img.naturalHeight || img.height || 1 }]);
        img.onerror = () => resolve([fn, { w: 1, h: 1 }]);
        img.src = `${API_BASE}/uploads/${fn}`;
      })));
      if (!cancelled) {
        const map = {};
        entries.forEach(([fn, dim]) => { map[fn] = dim; });
        setPhotoDims(map);
      }
    };
    if (selectedPhotos && selectedPhotos.length) loadDims();
    return () => { cancelled = true; };
  }, [selectedPhotos]);

  // Global mouse move/up for panning
  useEffect(() => {
    const onMove = (e) => {
      if (!panState) return;
      const { shapeIdx, startClientX, startClientY, startOffset, startVerticalOffset } = panState;

      // Don't process panning for videos
      if (displayMode === 'video' && videoAssignments[shapeIdx]) {
        return;
      }

      const deltaX = e.clientX - startClientX;
      const deltaY = e.clientY - startClientY;
      // compute bounds
      const shape = shapeItems[shapeIdx];
      const assignedIdx = shapeAssignments[shapeIdx];
      if (shape && assignedIdx != null) {
        const filename = selectedPhotos[assignedIdx];
        const dims = photoDims[filename] || { w: 1, h: 1 };
        const shapeW = shape.width;
        const shapeH = shape.height;
        const aspect = dims.w / Math.max(1, dims.h);
        const shapeAspect = shapeW / Math.max(1, shapeH);
        let imgW, imgH;
        if (aspect >= shapeAspect) {
          imgH = shapeH;
          imgW = imgH * aspect;
        } else {
          imgW = shapeW;
          imgH = imgW / aspect;
        }
        const scaleK = shapeScales[shapeIdx] || 1;
        imgW *= scaleK;
        imgH *= scaleK;

        // Horizontal bounds
        const maxOffsetX = Math.max(0, (imgW - shapeW) / 2);
        let newOffsetX = startOffset + deltaX;
        if (newOffsetX > maxOffsetX) newOffsetX = maxOffsetX;
        if (newOffsetX < -maxOffsetX) newOffsetX = -maxOffsetX;
        setShapeOffsets((prev) => prev.map((v, i) => (i === shapeIdx ? newOffsetX : v)));

        // Vertical bounds
        const maxOffsetY = Math.max(0, (imgH - shapeH) / 2);
        let newOffsetY = startVerticalOffset + deltaY;
        if (newOffsetY > maxOffsetY) newOffsetY = maxOffsetY;
        if (newOffsetY < -maxOffsetY) newOffsetY = -maxOffsetY;
        setShapeVerticalOffsets((prev) => prev.map((v, i) => (i === shapeIdx ? newOffsetY : v)));
      }
      if (dragStartRef.current) {
        const dx = Math.abs(e.clientX - dragStartRef.current.x);
        const dy = Math.abs(e.clientY - dragStartRef.current.y);
        if (dx > 3 || dy > 3) setIsDragging(true); // threshold 3px
      }
    };
    const onUp = () => {
      setPanState(null);
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp);
    };
  }, [panState, shapeAssignments, shapeItems, selectedPhotos, photoDims, shapeScales, displayMode, videoAssignments]);

  const captureFrameAsPNG = async () => {
    if (!workspace || !canvasRef.current) return;
  
    // Check if email is provided
    if (!emailInput.trim()) {
      setEmailStatus({
        success: false,
        message: 'Silakan masukkan email terlebih dahulu'
      });
      return;
    }
  
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
  
    console.log('Current state before capture:', {
      shapeScales,
      shapeOffsets,
      shapeVerticalOffsets,
      shapeAssignments,
      videoAssignments
    });
  
    try {
      // --- Tentukan ukuran background ---
      let bgWidth = 0;
      let bgHeight = 0;
      let bgX = 0;
  
      if (workspace.background_path) {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          bgImg.onload = resolve;
          bgImg.onerror = reject;
          bgImg.src = workspace.background_path.startsWith('http')
            ? workspace.background_path
            : `${API_BASE}${workspace.background_path}`;
        });
  
        const bgAspect = bgImg.width / bgImg.height;
        bgHeight = workspace.canvas_height || 600;
        bgWidth = bgHeight * bgAspect;
        bgX = 0;
      } else {
        bgWidth = workspace.canvas_width || 800;
        bgHeight = workspace.canvas_height || 600;
      }
  
      // --- Tambahkan faktor skala untuk HD ---
      const scaleFactor = 2; // bisa ubah ke 3 atau 4 kalau mau lebih tajam
      canvas.width = bgWidth * scaleFactor;
      canvas.height = bgHeight * scaleFactor;
  
      // Terapkan scaling ke context
      ctx.setTransform(scaleFactor, 0, 0, scaleFactor, 0, 0);
  
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
  
      // --- Gambar background ---
      if (workspace.background_path) {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          bgImg.onload = resolve;
          bgImg.onerror = reject;
          bgImg.src = workspace.background_path.startsWith('http')
            ? workspace.background_path
            : `${API_BASE}${workspace.background_path}`;
        });
  
        ctx.drawImage(bgImg, 0, 0, bgWidth, bgHeight);
      }
  
      const workspaceWidth = workspace.canvas_width || 800;
      const offsetX = (workspaceWidth - bgWidth) / 2;
  
      // --- Draw assets (back) ---
      const assetItems = items.filter(item => item.item_type === 'asset');
      for (const asset of assetItems) {
        const adjustedAssetX = asset.x - offsetX;
        if (
          adjustedAssetX + asset.width > 0 &&
          adjustedAssetX < bgWidth &&
          asset.y + asset.height > 0 &&
          asset.y < bgHeight &&
          (asset.layer === 'back' || !asset.layer) &&
          asset.src_path
        ) {
          const assetImg = new Image();
          assetImg.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            assetImg.onload = resolve;
            assetImg.onerror = reject;
            assetImg.src = asset.src_path.startsWith('http')
              ? asset.src_path
              : `${API_BASE}${asset.src_path}`;
          });
  
          ctx.save();
          ctx.translate(asset.x - offsetX + asset.width / 2, asset.y + asset.height / 2);
          ctx.rotate((asset.rotation_deg || 0) * Math.PI / 180);
          ctx.drawImage(assetImg, -asset.width / 2, -asset.height / 2, asset.width, asset.height);
          ctx.restore();
        }
      }
  
      // --- Draw shapes ---
      for (let i = 0; i < shapeItems.length; i++) {
        const shape = shapeItems[i];
        const assignedIdx = shapeAssignments[i];
        const adjustedShapeX = shape.x - offsetX;
  
        if (
          adjustedShapeX + shape.width > 0 &&
          adjustedShapeX < bgWidth &&
          shape.y + shape.height > 0 &&
          shape.y < bgHeight
        ) {
          ctx.save();
          ctx.translate(shape.x - offsetX + shape.width / 2, shape.y + shape.height / 2);
          ctx.rotate((shape.rotation_deg || 0) * Math.PI / 180);
  
          const borderRadius = shape.border_radius || 0;
          const x = -shape.width / 2;
          const y = -shape.height / 2;
          const w = shape.width;
          const h = shape.height;
  
          ctx.beginPath();
          ctx.moveTo(x + borderRadius, y);
          ctx.lineTo(x + w - borderRadius, y);
          ctx.quadraticCurveTo(x + w, y, x + w, y + borderRadius);
          ctx.lineTo(x + w, y + h - borderRadius);
          ctx.quadraticCurveTo(x + w, y + h, x + w - borderRadius, y + h);
          ctx.lineTo(x + borderRadius, y + h);
          ctx.quadraticCurveTo(x, y + h, x, y + h - borderRadius);
          ctx.lineTo(x, y + borderRadius);
          ctx.quadraticCurveTo(x, y, x + borderRadius, y);
          ctx.closePath();
          ctx.clip();
  
          if (assignedIdx != null && selectedPhotos[assignedIdx]) {
            const hasVideo = videoAssignments[i];
            const photoImg = new Image();
            photoImg.crossOrigin = 'anonymous';
            await new Promise((resolve, reject) => {
              photoImg.onload = resolve;
              photoImg.onerror = reject;
              const photoSrc = selectedPhotos[assignedIdx];
              photoImg.src = photoSrc.startsWith('http')
                ? photoSrc
                : `${API_BASE}/uploads/${photoSrc}`;
            });
  
            const dims = photoDims[selectedPhotos[assignedIdx]] || { w: photoImg.width, h: photoImg.height };
            const aspect = dims.w / Math.max(1, dims.h);
            const shapeAspect = w / Math.max(1, h);
            let imgW, imgH;
            if (aspect >= shapeAspect) {
              imgH = h;
              imgW = imgH * aspect;
            } else {
              imgW = w;
              imgH = imgW / aspect;
            }
  
            const scaleK = shapeScales[i] || 1;
            imgW *= scaleK;
            imgH *= scaleK;
  
            const leftBase = (w - imgW) / 2;
            const topBase = (h - imgH) / 2;
            const shapeOffsetX = shapeOffsets[i] || 0;
            const offsetY = shapeVerticalOffsets[i] || 0;
  
            ctx.save();
            ctx.scale(-1, 1);
            ctx.filter = getCanvasFilter(selectedFilter); // Terapkan filter hanya pada foto
            ctx.drawImage(
              photoImg,
              -(x + leftBase + shapeOffsetX + imgW),
              y + topBase + offsetY,
              imgW,
              imgH
            );
            ctx.filter = 'none'; // Reset filter setelah gambar foto
            ctx.restore();
          }
          ctx.restore();
        }
      }
  
      // --- Draw assets (front) ---
      for (const asset of assetItems) {
        const adjustedAssetX = asset.x - offsetX;
        if (
          adjustedAssetX + asset.width > 0 &&
          adjustedAssetX < bgWidth &&
          asset.y + asset.height > 0 &&
          asset.y < bgHeight &&
          asset.layer === 'front' &&
          asset.src_path
        ) {
          const assetImg = new Image();
          assetImg.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            assetImg.onload = resolve;
            assetImg.onerror = reject;
            assetImg.src = asset.src_path.startsWith('http')
              ? asset.src_path
              : `${API_BASE}${asset.src_path}`;
          });
  
          ctx.save();
          ctx.translate(asset.x - offsetX + asset.width / 2, asset.y + asset.height / 2);
          ctx.rotate((asset.rotation_deg || 0) * Math.PI / 180);
          ctx.drawImage(assetImg, -asset.width / 2, -asset.height / 2, asset.width, asset.height);
          ctx.restore();
        }
      }
  
      // --- Export sebagai PNG HD ---
      const pngDataUrl = canvas.toDataURL('image/png', 1.0);
  
      // --- Video frame check (tidak diubah) ---
      let videoFrameData = null;
      const hasVideoAssignments = Object.keys(videoAssignments).length > 0;
  
      if (displayMode === 'video' || hasVideoAssignments) {
        setIsCreatingVideo(true);
        try {
          videoFrameData = await createVideoFrame();
        } catch (error) {
          console.error('Failed to create video frame:', error);
        } finally {
          setIsCreatingVideo(false);
        }
      }
  
      const uploadResult = await uploadFinalFrameFiles(sessionId, pngDataUrl, videoFrameData);
  
      if (emailInput.trim()) {
        try {
          const finalOutputUrl = `${window.location.origin}/final-output2/${sessionId}`;
          const finalOutputUrl2 = `${window.location.origin}/final-output/${sessionId}`;

          const response = await fetch('https://api.laviopaperie.com/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: emailInput.trim(),
              sessionId: sessionId,
              finalOutputUrl: finalOutputUrl,
              finalOutputUrl2: finalOutputUrl2
            }),
          });
          const data = await response.json();
          setEmailStatus({
            success: data.success,
            message: data.success
              ? 'Email berhasil dikirim! Cek inbox Anda.'
              : data.message || 'Gagal mengirim email'
          });
        } catch (error) {
          console.error('Error sending email:', error);
          setEmailStatus({
            success: false,
            message: 'Gagal mengirim email. Silakan coba lagi.'
          });
        }
      }
  
      navigate(`/final-output2/${encodeURIComponent(sessionId)}`, {
        state: {
          imageData: pngDataUrl,
          videoFrameData: videoFrameData,
          sessionId,
          frameName: workspace.name,
          videoAssignments,
          shapeAssignments,
          shapeOffsets,
          shapeVerticalOffsets,
          shapeScales,
          workspace,
          items,
          uploadStatus: uploadResult ? 'success' : 'error',
          selectedFilter, // <-- pass filter
        }
      });
  
    } catch (error) {
      console.error('Error capturing frame:', error);
      alert('Error capturing frame. Please try again.');
    }
  };
  

  // Function to create video frame with MediaRecorder
  const createVideoFrame = async () => {
    if (!workspace || !canvasRef.current) return null;

    // Check if we have video assignments
    const hasVideoAssignments = Object.keys(videoAssignments).length > 0;

    if (hasVideoAssignments) {
      // If we have video assignments, create a composite video using the original videos
      return await createCompositeVideo();
    } else {
      // If no video assignments, create static frame video
      return await createStaticFrameVideo();
    }
  };

  // Function to create composite video using original videos
  const createCompositeVideo = async () => {
    if (!workspace || !canvasRef.current) return null;

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size
      let bgWidth = 0;
      let bgHeight = 0;

      if (workspace.background_path) {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          bgImg.onload = resolve;
          bgImg.onerror = reject;
          bgImg.src = workspace.background_path.startsWith('http')
            ? workspace.background_path
            : `${API_BASE}${workspace.background_path}`;
        });

        const bgAspect = bgImg.width / bgImg.height;
        bgHeight = workspace.canvas_height || 600;
        bgWidth = bgHeight * bgAspect;
      } else {
        bgWidth = workspace.canvas_width || 800;
        bgHeight = workspace.canvas_height || 600;
      }

      canvas.width = bgWidth;
      canvas.height = bgHeight;

      // Create stream from canvas with higher frame rate for smoother video
      const stream = canvas.captureStream(30); // 30 FPS for smoother video (60 can cause issues)

      // Check MediaRecorder support
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder is not supported in this browser');
      }

      // Try different MIME types
      const mimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm'
      ];

      let selectedMimeType = null;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        throw new Error('No supported video format found');
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });

      const chunks = [];
      let lastChunkTime = 0;

      return new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
            lastChunkTime = performance.now();
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: selectedMimeType });
          const url = URL.createObjectURL(blob);
          console.log('Composite video created successfully:', {
            blobSize: blob.size,
            mimeType: selectedMimeType,
            url: url
          });
          // Clear image cache to free memory
          imageCache.clear();
          // Force garbage collection if available
          if (window.gc) {
            window.gc();
          }
          resolve(url);
        };

        mediaRecorder.onerror = reject;

        // Start recording with larger timeslice for smoother video
        mediaRecorder.start(100);

        // Animation loop to draw frames with video content
        let startTime = performance.now(); // Use performance.now() for more precise timing
        const duration = 5000; // 5 seconds
        let frameCount = 0;
        let lastFrameTime = 0;
        const targetFrameTime = 16; // ~60 FPS for smoother video
        let animationId = null; // Track animation frame ID

        // Pre-load all images and videos
        const preloadContent = async () => {
          const imagePromises = [];
          const videoPromises = [];

          // Pre-load background
          if (workspace.background_path) {
            const bgImg = new Image();
            bgImg.crossOrigin = 'anonymous';
            imagePromises.push(new Promise((resolve, reject) => {
              bgImg.onload = () => {
                imageCache.set('background', bgImg);
                resolve();
              };
              bgImg.onerror = reject;
              bgImg.src = workspace.background_path.startsWith('http')
                ? workspace.background_path
                : `${API_BASE}${workspace.background_path}`;
            }));
          }

          // Pre-load assets
          const assetItems = items.filter(item => item.item_type === 'asset');
          for (const asset of assetItems) {
            if (asset.src_path) {
              const assetImg = new Image();
              assetImg.crossOrigin = 'anonymous';
              imagePromises.push(new Promise((resolve, reject) => {
                assetImg.onload = () => {
                  imageCache.set(`asset-${asset.id}`, assetImg);
                  resolve();
                };
                assetImg.onerror = reject;
                assetImg.src = asset.src_path.startsWith('http')
                  ? asset.src_path
                  : `${API_BASE}${asset.src_path}`;
              }));
            }
          }

          // Pre-load photos for non-video shapes
          const shapeItems = items.filter(item => item.item_type === 'shape');
          for (let i = 0; i < shapeItems.length; i++) {
            const assignedIdx = shapeAssignments[i];
            if (assignedIdx != null && selectedPhotos[assignedIdx] && !videoAssignments[i]) {
              const photoImg = new Image();
              photoImg.crossOrigin = 'anonymous';
              imagePromises.push(new Promise((resolve, reject) => {
                photoImg.onload = () => {
                  imageCache.set(`photo-${assignedIdx}`, photoImg);
                  resolve();
                };
                photoImg.onerror = reject;
                const photoSrc = selectedPhotos[assignedIdx];
                photoImg.src = photoSrc.startsWith('http')
                  ? photoSrc
                  : `${API_BASE}/uploads/${photoSrc}`;
              }));
            }
          }

          // Pre-load videos for video shapes with better loading strategy
          const videoShapeIndices = Object.keys(videoAssignments);
          for (const shapeIdx of videoShapeIndices) {
            const assignedIdx = shapeAssignments[parseInt(shapeIdx)];
            if (assignedIdx != null && selectedPhotos[assignedIdx]) {
              const videoElement = document.createElement('video');
              videoElement.src = getVideoUrl(sessionId, getPhotoNumberFromFilename(selectedPhotos[assignedIdx]));
              videoElement.muted = true;
              videoElement.loop = true;
              videoElement.crossOrigin = 'anonymous';
              videoElement.preload = 'auto';
              videoElement.playsInline = true;
              videoElement.autoplay = false; // Disable autoplay to prevent conflicts
              videoElement.controls = false;

              videoPromises.push(new Promise((resolve, reject) => {
                // Wait for both metadata and data to be loaded
                let metadataLoaded = false;
                let dataLoaded = false;

                const checkReady = () => {
                  if (metadataLoaded && dataLoaded) {
                    imageCache.set(`video-${shapeIdx}`, videoElement);
                    console.log(`Video ${shapeIdx} loaded successfully:`, videoElement.src);
                    resolve();
                  }
                };

                videoElement.onloadedmetadata = () => {
                  metadataLoaded = true;
                  checkReady();
                };

                videoElement.onloadeddata = () => {
                  dataLoaded = true;
                  checkReady();
                };

                // Pre-buffer video for smoother playback
                videoElement.oncanplaythrough = () => {
                  // Video is fully buffered and ready to play
                  console.log(`Video ${shapeIdx} fully buffered`);
                };

                videoElement.onerror = (error) => {
                  console.error(`Video ${shapeIdx} failed to load:`, error);
                  reject(error);
                };
              }));
            }
          }

          await Promise.all([...imagePromises, ...videoPromises]);
        };

        // Pre-load content and start animation
        preloadContent().then(() => {
          const animate = () => {
            const currentTime = performance.now(); // Use performance.now() for consistency
            const elapsed = currentTime - startTime;

            if (elapsed >= duration) {
              // Cancel any pending animation frame
              if (animationId) {
                cancelAnimationFrame(animationId);
              }
              // Request final data and stop
              mediaRecorder.requestData();
              setTimeout(() => {
                try {
                  mediaRecorder.stop();
                } catch (e) {
                  resolve(null);
                }
              }, 100);
              return;
            }

            // Do not throttle by time; rely on requestAnimationFrame cadence to avoid flicker

            // Draw the frame with video content (same as preview frame)
            drawFrameWithVideoContent(ctx, bgWidth, bgHeight, elapsed);
            frameCount++;
            lastFrameTime = currentTime;

            // Ensure video elements are playing for smooth frame extraction
            Object.keys(videoAssignments).forEach(shapeIdx => {
              const videoElement = imageCache.get(`video-${shapeIdx}`);
              if (videoElement && videoElement.paused) {
                videoElement.play().catch(() => { }); // Ignore autoplay errors
              }
            });

            // Periodic memory cleanup to prevent pauses
            if (frameCount % 60 === 0) {
              // Force minor garbage collection if available
              if (window.gc) {
                window.gc();
              }
              // Clear cache if needed
              clearCacheIfNeeded();
            }

            // Debug: Log every 30 frames
            if (frameCount % 30 === 0) {
              console.log(`Composite video generation progress: ${frameCount} frames, ${elapsed}ms elapsed`);
            }

            // Use requestAnimationFrame only for consistent cadence
            animationId = requestAnimationFrame(animate);
          };

          // Start all preloaded videos once, then start animation loop
          Object.keys(videoAssignments).forEach(shapeIdx => {
            const videoElement = imageCache.get(`video-${shapeIdx}`);
            if (videoElement && videoElement.paused) {
              videoElement.play().catch(() => { });
            }
          });
          animate();
        });
      });

    } catch (error) {
      console.error('Error creating composite video:', error);
      return null;
    }
  };

  // Function to create static frame video (original implementation)
  const createStaticFrameVideo = async () => {

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size
      let bgWidth = 0;
      let bgHeight = 0;

      if (workspace.background_path) {
        const bgImg = new Image();
        bgImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          bgImg.onload = resolve;
          bgImg.onerror = reject;
          bgImg.src = workspace.background_path.startsWith('http')
            ? workspace.background_path
            : `${API_BASE}${workspace.background_path}`;
        });

        const bgAspect = bgImg.width / bgImg.height;
        bgHeight = workspace.canvas_height || 600;
        bgWidth = bgHeight * bgAspect;
      } else {
        bgWidth = workspace.canvas_width || 800;
        bgHeight = workspace.canvas_height || 600;
      }

      canvas.width = bgWidth;
      canvas.height = bgHeight;

      // Create stream from canvas with higher frame rate for smoother video
      const stream = canvas.captureStream(30); // 30 FPS for smoother video (60 can cause issues)

      // Check MediaRecorder support
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder is not supported in this browser');
      }

      // Try different MIME types
      const mimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm'
      ];

      let selectedMimeType = null;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        throw new Error('No supported video format found');
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });

      const chunks = [];

      return new Promise((resolve, reject) => {
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: selectedMimeType });
          const url = URL.createObjectURL(blob);
          console.log('Video created successfully:', {
            blobSize: blob.size,
            mimeType: selectedMimeType,
            url: url
          });
          // Clear image cache to free memory
          imageCache.clear();
          // Force garbage collection if available
          if (window.gc) {
            window.gc();
          }
          resolve(url);
        };

        mediaRecorder.onerror = reject;

        // Start recording with larger timeslice for smoother video
        mediaRecorder.start(100);

        // Pre-load all images for smoother rendering
        const preloadImages = async () => {
          const imagePromises = [];

          // Pre-load background
          if (workspace.background_path) {
            const bgImg = new Image();
            bgImg.crossOrigin = 'anonymous';
            imagePromises.push(new Promise((resolve, reject) => {
              bgImg.onload = () => {
                imageCache.set('background', bgImg);
                resolve();
              };
              bgImg.onerror = reject;
              bgImg.src = workspace.background_path.startsWith('http')
                ? workspace.background_path
                : `${API_BASE}${workspace.background_path}`;
            }));
          }

          // Pre-load all photos and assets
          const assetItems = items.filter(item => item.item_type === 'asset');
          for (const asset of assetItems) {
            if (asset.src_path) {
              const assetImg = new Image();
              assetImg.crossOrigin = 'anonymous';
              imagePromises.push(new Promise((resolve, reject) => {
                assetImg.onload = () => {
                  imageCache.set(`asset-${asset.id}`, assetImg);
                  resolve();
                };
                assetImg.onerror = reject;
                assetImg.src = asset.src_path.startsWith('http')
                  ? asset.src_path
                  : `${API_BASE}${asset.src_path}`;
              }));
            }
          }

          const shapeItems = items.filter(item => item.item_type === 'shape');
          for (let i = 0; i < shapeItems.length; i++) {
            const assignedIdx = shapeAssignments[i];
            if (assignedIdx != null && selectedPhotos[assignedIdx]) {
              const photoImg = new Image();
              photoImg.crossOrigin = 'anonymous';
              imagePromises.push(new Promise((resolve, reject) => {
                photoImg.onload = () => {
                  imageCache.set(`photo-${assignedIdx}`, photoImg);
                  resolve();
                };
                photoImg.onerror = reject;
                const photoSrc = selectedPhotos[assignedIdx];
                photoImg.src = photoSrc.startsWith('http')
                  ? photoSrc
                  : `${API_BASE}/uploads/${photoSrc}`;
              }));
            }
          }

          await Promise.all(imagePromises);
        };

        // Pre-load images before starting animation
        preloadImages().then(() => {
          // Animation loop to draw frames for 5 seconds with higher frame rate
          let startTime = performance.now(); // Use performance.now() for consistency
          const duration = 5000; // 5 seconds
          let frameCount = 0;
          let animationId = null;

          const animate = () => {
            const elapsed = performance.now() - startTime;

            if (elapsed >= duration) {
              // Cancel any pending animation frame
              if (animationId) {
                cancelAnimationFrame(animationId);
              }
              // Request final data and stop
              mediaRecorder.requestData();
              setTimeout(() => {
                try {
                  mediaRecorder.stop();
                } catch (e) {
                  // If stop fails, resolve anyway
                  resolve(null);
                }
              }, 100);
              return;
            }

            // Draw the frame with current state (same as PNG capture but for video)
            drawFrameToCanvasForVideo(ctx, bgWidth, bgHeight);
            frameCount++;

            // Debug: Log every 30 frames (every 0.5 seconds at 60fps)
            if (frameCount % 30 === 0) {
              console.log(`Video generation progress: ${frameCount} frames, ${elapsed}ms elapsed`);
            }

            // Use requestAnimationFrame for smooth 60fps rendering
            // Use requestIdleCallback if available for better performance
            if (window.requestIdleCallback) {
              requestIdleCallback(() => {
                animationId = requestAnimationFrame(animate);
              }, { timeout: 16 });
            } else {
              animationId = requestAnimationFrame(animate);
            }
          };

          // Start animation after a short delay to ensure recording has started
          setTimeout(() => {
            animate();
          }, 100);
        });
      });

    } catch (error) {
      console.error('Error creating video frame:', error);
      return null;
    }
  };

  // Cache for pre-loaded images to avoid repeated loading
  const imageCache = new Map();

  // Memory management for cache
  const clearCacheIfNeeded = () => {
    if (imageCache.size > 50) { // Limit cache size
      const keys = Array.from(imageCache.keys());
      // Keep only the most recent 30 items
      keys.slice(0, keys.length - 30).forEach(key => {
        imageCache.delete(key);
      });
    }
  };

  // Function to upload final frame files to API
  const uploadFinalFrameFiles = async (sessionId, imageData, videoFrameData) => {
    try {
      const formData = new FormData();
      formData.append('sessionId', sessionId);

      // Convert image data URL to blob and add to form data
      if (imageData) {
        const imageResponse = await fetch(imageData);
        const imageBlob = await imageResponse.blob();
        formData.append('files', imageBlob, `${sessionId}_final_frame.png`);
      }

      // Convert video blob URL to blob and add to form data
      if (videoFrameData) {
        const videoResponse = await fetch(videoFrameData);
        const videoBlob = await videoResponse.blob();
        formData.append('files', videoBlob, `${sessionId}_final_frame_video.webm`);
      }

      const response = await fetch(`${API_BASE}/api/upload-session`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json();
      console.log('Final frame files uploaded successfully:', result);
      return result;

    } catch (error) {
      console.error('Error uploading final frame files:', error);
      // Don't throw error to prevent blocking navigation
      // Just log it for debugging
    }
  };

  // Helper function to draw frame with video content (optimized for smooth playback)
  const drawFrameWithVideoContent = (ctx, bgWidth, bgHeight, elapsed) => {
    // Enable hardware acceleration for smoother rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Clear canvas efficiently
    ctx.clearRect(0, 0, bgWidth, bgHeight);

    // Optimize canvas for better performance
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';

    // Draw background if exists
    if (workspace.background_path) {
      const bgImg = imageCache.get('background');
      if (bgImg) {
        ctx.drawImage(bgImg, 0, 0, bgWidth, bgHeight);
      }
    }

    // Calculate offset
    const workspaceWidth = workspace.canvas_width || 800;
    const offsetX = (workspaceWidth - bgWidth) / 2;

    // Draw assets (behind shapes)
    const assetItems = items.filter(item => item.item_type === 'asset');
    for (const asset of assetItems) {
      const adjustedAssetX = asset.x - offsetX;
      if (adjustedAssetX + asset.width > 0 && adjustedAssetX < bgWidth &&
        asset.y + asset.height > 0 && asset.y < bgHeight &&
        (asset.layer === 'back' || !asset.layer) && asset.src_path) {
        const assetImg = imageCache.get(`asset-${asset.id}`);
        if (assetImg) {
          ctx.save();
          ctx.translate(asset.x - offsetX + asset.width / 2, asset.y + asset.height / 2);
          ctx.rotate((asset.rotation_deg || 0) * Math.PI / 180);
          ctx.drawImage(assetImg, -asset.width / 2, -asset.height / 2, asset.width, asset.height);
          ctx.restore();
        }
      }
    }

    // Draw shapes with assigned photos/videos
    const shapeItems = items.filter(item => item.item_type === 'shape');
    for (let i = 0; i < shapeItems.length; i++) {
      const shape = shapeItems[i];
      const assignedIdx = shapeAssignments[i];

      const adjustedShapeX = shape.x - offsetX;
      if (adjustedShapeX + shape.width > 0 && adjustedShapeX < bgWidth &&
        shape.y + shape.height > 0 && shape.y < bgHeight) {

        ctx.save();
        ctx.translate(shape.x - offsetX + shape.width / 2, shape.y + shape.height / 2);
        ctx.rotate((shape.rotation_deg || 0) * Math.PI / 180);

        const borderRadius = shape.border_radius || 0;
        const x = -shape.width / 2;
        const y = -shape.height / 2;
        const w = shape.width;
        const h = shape.height;

        ctx.beginPath();
        ctx.moveTo(x + borderRadius, y);
        ctx.lineTo(x + w - borderRadius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + borderRadius);
        ctx.lineTo(x + w, y + h - borderRadius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - borderRadius, y + h);
        ctx.lineTo(x + borderRadius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - borderRadius);
        ctx.lineTo(x, y + borderRadius);
        ctx.quadraticCurveTo(x, y, x + borderRadius, y);
        ctx.closePath();
        ctx.clip();

        if (assignedIdx != null && selectedPhotos[assignedIdx]) {
          const hasVideo = videoAssignments[i];

          if (hasVideo) {
            // Use pre-loaded video element from cache with smooth frame extraction
            const videoElement = imageCache.get(`video-${i}`);
            if (videoElement && videoElement.readyState >= 2) {
              // Calculate smooth video time with better synchronization
              const videoTime = (elapsed / 1000) % Math.max(1, videoElement.duration);

              // Do not seek per frame to avoid flicker; just draw the currently playing frame
              try {
                // Draw the video frame with smooth rendering
                ctx.save();
                ctx.scale(-1, 1);

                const dims = photoDims[selectedPhotos[assignedIdx]] || { w: 1, h: 1 };
                const aspect = dims.w / Math.max(1, dims.h);
                const shapeAspect = w / Math.max(1, h);
                let imgW, imgH;
                if (aspect >= shapeAspect) {
                  imgH = h;
                  imgW = imgH * aspect;
                } else {
                  imgW = w;
                  imgH = imgW / aspect;
                }

                const leftBase = (w - imgW) / 2;
                const topBase = (h - imgH) / 2;

                // Draw video frame with smooth interpolation and better error handling
                if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
                  ctx.drawImage(videoElement, -(x + leftBase + imgW), y + topBase, imgW, imgH);
                }
                ctx.restore();
              } catch (error) {
                // Fallback to photo if video frame extraction fails
                const photoImg = imageCache.get(`photo-${assignedIdx}`);
                if (photoImg) {
                  const dims = photoDims[selectedPhotos[assignedIdx]] || { w: photoImg.width, h: photoImg.height };
                  const aspect = dims.w / Math.max(1, dims.h);
                  const shapeAspect = w / Math.max(1, h);
                  let imgW, imgH;
                  if (aspect >= shapeAspect) {
                    imgH = h;
                    imgW = imgH * aspect;
                  } else {
                    imgW = w;
                    imgH = imgW / aspect;
                  }

                  const leftBase = (w - imgW) / 2;
                  const topBase = (h - imgH) / 2;

                  ctx.save();
                  ctx.scale(-1, 1);
                  ctx.drawImage(photoImg, -(x + leftBase + imgW), y + topBase, imgW, imgH);
                  ctx.restore();
                }
              }
            } else {
              // Fallback to photo if video not loaded
              const photoImg = imageCache.get(`photo-${assignedIdx}`);
              if (photoImg) {
                const dims = photoDims[selectedPhotos[assignedIdx]] || { w: photoImg.width, h: photoImg.height };
                const aspect = dims.w / Math.max(1, dims.h);
                const shapeAspect = w / Math.max(1, h);
                let imgW, imgH;
                if (aspect >= shapeAspect) {
                  imgH = h;
                  imgW = imgH * aspect;
                } else {
                  imgW = w;
                  imgH = imgW / aspect;
                }

                const scaleK = shapeScales[i] || 1;
                imgW *= scaleK;
                imgH *= scaleK;

                const leftBase = (w - imgW) / 2;
                const topBase = (h - imgH) / 2;
                const shapeOffsetX = shapeOffsets[i] || 0;
                const offsetY = shapeVerticalOffsets[i] || 0;

                // Mirror the photo horizontally
                ctx.save();
                ctx.scale(-1, 1);
                ctx.filter = getCanvasFilter(selectedFilter); // Terapkan filter hanya pada foto
                ctx.drawImage(
                  photoImg,
                  -(x + leftBase + shapeOffsetX + imgW),
                  y + topBase + offsetY,
                  imgW,
                  imgH
                );
                ctx.filter = 'none'; // Reset filter setelah gambar foto
                ctx.restore();
              }
            }
          } else {
            // For photo shapes, use user settings (scale, offset, etc.)
            const photoImg = imageCache.get(`photo-${assignedIdx}`);
            if (photoImg) {
              const dims = photoDims[selectedPhotos[assignedIdx]] || { w: photoImg.width, h: photoImg.height };
              const aspect = dims.w / Math.max(1, dims.h);
              const shapeAspect = w / Math.max(1, h);
              let imgW, imgH;
              if (aspect >= shapeAspect) {
                imgH = h;
                imgW = imgH * aspect;
              } else {
                imgW = w;
                imgH = imgW / aspect;
              }

              const scaleK = shapeScales[i] || 1;
              imgW *= scaleK;
              imgH *= scaleK;

              const leftBase = (w - imgW) / 2;
              const topBase = (h - imgH) / 2;
              const shapeOffsetX = shapeOffsets[i] || 0;
              const offsetY = shapeVerticalOffsets[i] || 0;

              // Mirror the photo horizontally
              ctx.save();
              ctx.scale(-1, 1);
              ctx.filter = getCanvasFilter(selectedFilter); // Terapkan filter hanya pada foto
              ctx.drawImage(
                photoImg,
                -(x + leftBase + shapeOffsetX + imgW),
                y + topBase + offsetY,
                imgW,
                imgH
              );
              ctx.filter = 'none'; // Reset filter setelah gambar foto
              ctx.restore();
            }
          }
        }

        ctx.restore();
      }
    }

    // Draw front assets
    for (const asset of assetItems) {
      const adjustedAssetX = asset.x - offsetX;
      if (adjustedAssetX + asset.width > 0 && adjustedAssetX < bgWidth &&
        asset.y + asset.height > 0 && asset.y < bgHeight &&
        asset.layer === 'front' && asset.src_path) {
        const assetImg = imageCache.get(`asset-${asset.id}`);
        if (assetImg) {
          ctx.save();
          ctx.translate(asset.x - offsetX + asset.width / 2, asset.y + asset.height / 2);
          ctx.rotate((asset.rotation_deg || 0) * Math.PI / 180);
          ctx.drawImage(assetImg, -asset.width / 2, -asset.height / 2, asset.width, asset.height);
          ctx.restore();
        }
      }
    }

    // Restore canvas state
    ctx.restore();
  };

  // Helper function to draw frame to canvas for video (with current user settings)
  const drawFrameToCanvasForVideo = (ctx, bgWidth, bgHeight) => {
    // Enable hardware acceleration for smoother rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Clear canvas
    ctx.clearRect(0, 0, bgWidth, bgHeight);

    // Draw background if exists
    if (workspace.background_path) {
      const bgImg = imageCache.get('background');
      if (bgImg) {
        ctx.drawImage(bgImg, 0, 0, bgWidth, bgHeight);
      }
    }

    // Calculate offset
    const workspaceWidth = workspace.canvas_width || 800;
    const offsetX = (workspaceWidth - bgWidth) / 2;

    // Draw assets (behind shapes)
    const assetItems = items.filter(item => item.item_type === 'asset');
    for (const asset of assetItems) {
      const adjustedAssetX = asset.x - offsetX;
      if (adjustedAssetX + asset.width > 0 && adjustedAssetX < bgWidth &&
        asset.y + asset.height > 0 && asset.y < bgHeight &&
        (asset.layer === 'back' || !asset.layer) && asset.src_path) {
        const assetImg = imageCache.get(`asset-${asset.id}`);
        if (assetImg) {
          ctx.save();
          ctx.translate(asset.x - offsetX + asset.width / 2, asset.y + asset.height / 2);
          ctx.rotate((asset.rotation_deg || 0) * Math.PI / 180);
          ctx.drawImage(assetImg, -asset.width / 2, -asset.height / 2, asset.width, asset.height);
          ctx.restore();
        }
      }
    }

    // Draw shapes with assigned photos/videos
    const shapeItems = items.filter(item => item.item_type === 'shape');
    for (let i = 0; i < shapeItems.length; i++) {
      const shape = shapeItems[i];
      const assignedIdx = shapeAssignments[i];

      const adjustedShapeX = shape.x - offsetX;
      if (adjustedShapeX + shape.width > 0 && adjustedShapeX < bgWidth &&
        shape.y + shape.height > 0 && shape.y < bgHeight) {

        ctx.save();
        ctx.translate(shape.x - offsetX + shape.width / 2, shape.y + shape.height / 2);
        ctx.rotate((shape.rotation_deg || 0) * Math.PI / 180);

        const borderRadius = shape.border_radius || 0;
        const x = -shape.width / 2;
        const y = -shape.height / 2;
        const w = shape.width;
        const h = shape.height;

        ctx.beginPath();
        ctx.moveTo(x + borderRadius, y);
        ctx.lineTo(x + w - borderRadius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + borderRadius);
        ctx.lineTo(x + w, y + h - borderRadius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - borderRadius, y + h);
        ctx.lineTo(x + borderRadius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - borderRadius);
        ctx.lineTo(x, y + borderRadius);
        ctx.quadraticCurveTo(x, y, x + borderRadius, y);
        ctx.closePath();
        ctx.clip();

        if (assignedIdx != null && selectedPhotos[assignedIdx]) {
          const hasVideo = videoAssignments[i];

          if (hasVideo && displayMode === 'video') {
            // For video mode, use the corresponding photo as the frame
            const photoImg = imageCache.get(`photo-${assignedIdx}`);
            if (photoImg) {
              const dims = photoDims[selectedPhotos[assignedIdx]] || { w: photoImg.width, h: photoImg.height };
              const aspect = dims.w / Math.max(1, dims.h);
              const shapeAspect = w / Math.max(1, h);
              let imgW, imgH;
              if (aspect >= shapeAspect) {
                imgH = h;
                imgW = imgH * aspect;
              } else {
                imgW = w;
                imgH = imgW / aspect;
              }

              const leftBase = (w - imgW) / 2;
              const topBase = (h - imgH) / 2;

              // Mirror the photo horizontally for video frame
              ctx.save();
              ctx.scale(-1, 1);
              ctx.filter = getCanvasFilter(selectedFilter); // Terapkan filter hanya pada foto
              ctx.drawImage(
                photoImg,
                -(x + leftBase + imgW),
                y + topBase,
                imgW,
                imgH
              );
              ctx.filter = 'none'; // Reset filter setelah gambar foto
              ctx.restore();
            }
          } else {
            // For photo mode or when no video assignment, use user settings (scale, offset, etc.)
            const photoImg = imageCache.get(`photo-${assignedIdx}`);
            if (photoImg) {
              const dims = photoDims[selectedPhotos[assignedIdx]] || { w: photoImg.width, h: photoImg.height };
              const aspect = dims.w / Math.max(1, dims.h);
              const shapeAspect = w / Math.max(1, h);
              let imgW, imgH;
              if (aspect >= shapeAspect) {
                imgH = h;
                imgW = imgH * aspect;
              } else {
                imgW = w;
                imgH = imgW / aspect;
              }

              const scaleK = shapeScales[i] || 1;
              imgW *= scaleK;
              imgH *= scaleK;

              const leftBase = (w - imgW) / 2;
              const topBase = (h - imgH) / 2;
              const shapeOffsetX = shapeOffsets[i] || 0;
              const offsetY = shapeVerticalOffsets[i] || 0;

              // Mirror the photo horizontally
              ctx.save();
              ctx.scale(-1, 1);
              ctx.filter = getCanvasFilter(selectedFilter); // Terapkan filter hanya pada foto
              ctx.drawImage(
                photoImg,
                -(x + leftBase + shapeOffsetX + imgW),
                y + topBase + offsetY,
                imgW,
                imgH
              );
              ctx.filter = 'none'; // Reset filter setelah gambar foto
              ctx.restore();
            }
          }
        }

        ctx.restore();
      }
    }

    // Draw front assets
    for (const asset of assetItems) {
      const adjustedAssetX = asset.x - offsetX;
      if (adjustedAssetX + asset.width > 0 && adjustedAssetX < bgWidth &&
        asset.y + asset.height > 0 && asset.y < bgHeight &&
        asset.layer === 'front' && asset.src_path) {
        const assetImg = imageCache.get(`asset-${asset.id}`);
        if (assetImg) {
          ctx.save();
          ctx.translate(asset.x - offsetX + asset.width / 2, asset.y + asset.height / 2);
          ctx.rotate((asset.rotation_deg || 0) * Math.PI / 180);
          ctx.drawImage(assetImg, -asset.width / 2, -asset.height / 2, asset.width, asset.height);
          ctx.restore();
        }
      }
    }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await fetch(`https://api.laviopaperie.com/uploads/${filename}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Gagal mengunduh file');
    }
  };

  const downloadAll = async () => {
    try {
      const zip = new JSZip();

      // Download semua foto terpilih
      for (const filename of selectedPhotos) {
        const response = await fetch(`https://api.laviopaperie.com/uploads/${filename}`);
        if (response.ok) {
          const blob = await response.blob();
          zip.file(filename, blob);
        }
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sessionId}_selected_photos.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error creating zip:', err);
      alert('Gagal membuat file ZIP');
    }
  };

  const goBackToSelection = () => {
    navigate(`/camera-results/${sessionId}`);
  };

  const startNewSession = () => {
    navigate('/camera-session');
  };

  const sendEmail = async () => {
    if (!emailInput.trim()) {
      setEmailStatus({
        success: false,
        message: 'Email tidak boleh kosong'
      });
      return;
    }

    setIsSendingEmail(true);
    setEmailStatus(null);

    try {
      // Get the final output URL (you can modify this based on your needs)
      const finalOutputUrl = `${window.location.origin}/final-output2/${sessionId}`;

      const finalOutputUrl2 = `${window.location.origin}/final-output/${sessionId}`;
      const response = await fetch('https://api.laviopaperie.com/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailInput.trim(),
          sessionId: sessionId,
          finalOutputUrl2: finalOutputUrl2
        }),
      });

      const data = await response.json();

      if (data.success) {
        setEmailStatus({
          success: true,
          message: 'Email berhasil dikirim! Cek inbox Anda.'
        });
        setEmailInput(''); // Clear input after successful send
      } else {
        setEmailStatus({
          success: false,
          message: data.message || 'Gagal mengirim email'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus({
        success: false,
        message: 'Gagal mengirim email. Silakan coba lagi.'
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    // Auto refresh page 1x saat pertama kali diakses
    if (!sessionStorage.getItem('selectedResultsRefreshed')) {
      sessionStorage.setItem('selectedResultsRefreshed', '1');
      window.location.reload();
    }
  }, []);

  // Helper to get CSS filter string from selectedFilter
  const getCssFilter = (filter) => {
    switch (filter) {
      case 'bnw': return 'grayscale(1)';
      case 'sepia': return 'sepia(1)';
      case 'cool': return 'contrast(1.1) brightness(1.05) hue-rotate(-20deg)';
      case 'warm': return 'contrast(1.1) brightness(1.08) sepia(0.3) hue-rotate(20deg)';
      default: return 'none';
    }
  };

  // In captureFrameAsPNG, before drawing each photo (ctx.drawImage), set ctx.filter based on selectedFilter
  // Add helper to get canvas filter string
  const getCanvasFilter = (filter) => {
    switch (filter) {
      case 'bnw': return 'grayscale(1)';
      case 'sepia': return 'sepia(1)';
      case 'cool': return 'contrast(1.1) brightness(1.05) hue-rotate(-20deg)';
      case 'warm': return 'contrast(1.1) brightness(1.08) sepia(0.3) hue-rotate(20deg)';
      default: return 'none';
    }
  };

  // Handler untuk pinch-to-zoom
  const handleTouchStart = (e, sIdx) => {
    if (e.touches && e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      setPinchState({
        shapeIdx: sIdx,
        startDist: dist,
        startScale: shapeScales[sIdx] || 1,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (pinchState && e.touches && e.touches.length === 2) {
      const [t1, t2] = e.touches;
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const scaleChange = dist / pinchState.startDist;
      const newScale = Math.max(1, Math.min(4, pinchState.startScale * scaleChange)); // Clamp scale 1-4
      setShapeScales((prev) =>
        prev.map((v, i) => (i === pinchState.shapeIdx ? newScale : v))
      );
      e.preventDefault(); // Mencegah scroll browser
    }
  };

  const handleTouchEnd = (e) => {
    if (!e.touches || e.touches.length < 2) setPinchState(null);
  };

  // Urutkan shape aktif ke paling akhir agar selalu di atas
  let sortedItems = [...allItems];
  if (editingShapeId) {
    const idx = sortedItems.findIndex(it => it.id === editingShapeId);
    if (idx !== -1) {
      const [activeShape] = sortedItems.splice(idx, 1);
      sortedItems.push(activeShape);
    }
  }

  if (loading) {
    return (
      <div className="camera-results">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Memuat hasil pilihan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camera-results">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="back-button" onClick={() => navigate(`/camera-results/${sessionId}`)}>
            Kembali ke Hasil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="selected-results">

      <div className="selected-results-content">
        <div className="selected-photos-section">
          <div className="selected-photos-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'}}>
            <span className="selected-photos-title">Selected Photos</span>
            {/* Countdown Timer Rectangle */}
            <div
              style={{
                minWidth: 70,
                minHeight: 38,
                padding: '0 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: countdown <= 15 ? 'linear-gradient(90deg, #ffb6b9 0%, #ff6b81 100%)' : 'linear-gradient(90deg, #ccb4d6 0%, #f8f4ff 100%)',
                color: countdown <= 15 ? '#fff' : '#764ba2',
                fontFamily: 'Poppins, Arial, sans-serif',
                fontWeight: 700,
                fontSize: '1.1rem',
                borderRadius: '16px',
                boxShadow: countdown <= 15 ? '0 0 8px #ff6b81aa' : '0 2px 8px #ccb4d633',
                border: countdown <= 15 ? '2px solid #ff6b81' : '2px solid #ccb4d6',
                letterSpacing: '1px',
                userSelect: 'none',
                transition: 'all 0.3s',
                marginLeft: 'auto',
              }}
            >
              {formatCountdown(countdown)}
            </div>
          </div>

          {/* Email Input - Moved to top */}
          <div className="email-section">
            <div className="email-title">
              Drop your email, we'll send the photos once you're done!💖
            </div>
            <input
              type="email"
              placeholder="Email here..."
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="email-input"
            />
            {emailStatus && (
              <div className={`email-status ${emailStatus.success ? 'success' : 'error'}`}>
                {emailStatus.message}
              </div>
            )}
          </div>

          <div className="selected-photos-grid selected-photos-grid--2col">
            {selectedPhotos.map((filename, index) => {
              const isDone = shapeAssignments.includes(index);
              return (
                <div
                  key={index}
                  className={`selected-photo-item ${activePhotoIdx === index ? 'active' : ''}`}
                  onClick={() => {
                    if (isDone) {
                      Swal.fire({
                        icon: 'info',
                        title: 'Photo already picked',
                        text: "This photo's already on another frame. Pick a new one or delete it first ✨",
                        confirmButtonColor: '#764ba2',
                        confirmButtonText: 'OK'
                      });
                      return;
                    }
                    setActivePhotoIdx(index);
                  }}
                  style={{ position: 'relative' }}
                >
                  <img
                    src={`${API_BASE}/uploads/${filename}`}
                    alt={`Foto Terpilih ${index + 1}`}
                    className="selected-photo-img-small"
                  />
                  {isDone && (
                    <div className="photo-done-overlay"></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="selected-photos-info">
            Pick a photo, then tap a shape to drop it in frame and tap the video button.
            {displayMode === 'video' && ' Watch the auto video from your pic'}
            {isCreatingVideo && ' ⏳Wait for a 5 seconds...'}
            {displayMode === 'video' && Object.keys(videoAssignments).length > 0 && '✨🎥'}
          </div>

          {/* Done button at bottom right */}
          <div className="done-button-container">
            <button
              onClick={captureFrameAsPNG}
              disabled={isCreatingVideo || !emailInput.trim()}
              className={`done-button ${displayMode === 'video' ? 'video-mode' : ''}`}
            >
              {isCreatingVideo ? 'Creating Video...' : 'Done'}
            </button>
          </div>
        </div>

        <div className="final-frame-section">
          <div className="final-frame-title">
            <span>Preview Frame</span>
            {/* Photo/Video toggle moved to preview frame section */}
            <button
              onClick={() => setDisplayMode(displayMode === 'photo' ? 'video' : 'photo')}
              className={`preview-mode-toggle ${displayMode === 'video' ? 'video-mode' : 'photo-mode'}`}
            >
              {displayMode === 'photo' ? '🎥 Video' : '📷 Photo'}
            </button>
          </div>
          <div style={{display:'flex',height:'100%'}}>
            {/* Frame preview */}
            {workspace ? (
              <div ref={wrapperRef} className="frame-preview-container" style={{flex:1,position:'relative'}}>
                <div
                  className="frame-wrapper"
                  style={{
                    width: (workspace.canvas_width || 1000),
                    height: (workspace.canvas_height || 700),
                    transform: `translate(-50%, -50%) scale(${scale})`,
                  }}
                >
                  {workspace.background_path && (
                    <img
                      src={`${API_BASE}${workspace.background_path}`}
                      alt="bg"
                      className="frame-background"
                    />
                  )}
                  {sortedItems.map((it) => {
                    const isEditing = editingShapeId === it.id;
                    const baseStyle = {
                      position: 'absolute',
                      left: it.x,
                      top: it.y,
                      width: it.width,
                      height: it.height,
                      transform: `rotate(${it.rotation_deg || 0}deg)`,
                      transformOrigin: 'center center',
                      overflow: 'hidden',
                      borderRadius: it.item_type === 'asset' ? 0 : (it.border_radius || 0),
                      zIndex: isEditing ? 999 : (it.layer === 'back' ? 1 : 3),
                    };
                    if (it.item_type === 'asset') {
                      return (
                        <div key={`asset-${it.id}`} className="frame-item asset" style={baseStyle}>
                          {it.src_path ? (
                            <img src={`${API_BASE}${it.src_path}`} alt={`asset-${it.id}`} />
                          ) : null}
                        </div>
                      );
                    }
                    const sIdx = shapeItems.findIndex(s => s.id === it.id);
                    const assignedIdx = sIdx >= 0 ? shapeAssignments[sIdx] : null;
                    const assignedPhoto = (assignedIdx != null && selectedPhotos[assignedIdx]) ? `${API_BASE}/uploads/${selectedPhotos[assignedIdx]}` : null;
                    const isActive = isEditing;
                    return (
                      <div
                        key={`shape-${it.id}`}
                        className="frame-item shape"
                        style={baseStyle}
                        onClick={() => {
                          if (isDragging) return;
                          // Jika belum ada foto di shape, klik akan assign foto yang sedang aktif
                          if (!assignedPhoto) {
                          if (activePhotoIdx == null || sIdx < 0) return;
                          if (shapeAssignments.includes(activePhotoIdx)) return;
                          const next = shapeAssignments.slice();
                          next[sIdx] = activePhotoIdx;
                          setShapeAssignments(next);
                          setShapeOffsets((prev) => prev.map((v, i) => (i === sIdx ? 0 : v)));
                          setShapeVerticalOffsets((prev) => prev.map((v, i) => (i === sIdx ? 0 : v)));
                          const selectedPhotoFilename = selectedPhotos[activePhotoIdx];
                          const photoNumber = getPhotoNumberFromFilename(selectedPhotoFilename);
                          if (photoNumber) {
                            const videoFilename = getVideoFilename(sessionId, photoNumber);
                            const newVideoAssignments = { ...videoAssignments };
                            newVideoAssignments[sIdx] = videoFilename;
                            setVideoAssignments(newVideoAssignments);
                          }
                            // Setelah assign, otomatis masuk ke mode edit shape ini
                            setActiveShapeId(it.id);
                          setEditingShapeId(it.id);
                            return;
                          }

                          // Jika shape sudah punya foto, klik pertama akan masuk ke mode edit
                          if (!isEditing) {
                            setActiveShapeId(it.id);
                            setEditingShapeId(it.id);
                          }
                        }}
                      >
                        {/* Saat sedang mengedit satu shape, shape lain diberikan overlay gelap */}
                        {editingShapeId && editingShapeId !== it.id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(0,0,0,0.45)',
                              zIndex: 5,
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                        {assignedPhoto ? (
                          (() => {
                            const filename = selectedPhotos[assignedIdx];
                            const dims = photoDims[filename] || { w: 1, h: 1 };
                            const shapeW = it.width;
                            const shapeH = it.height;
                            const aspect = dims.w / Math.max(1, dims.h);
                            const shapeAspect = shapeW / Math.max(1, shapeH);
                            let imgW, imgH;
                            if (aspect >= shapeAspect) {
                              imgH = shapeH;
                              imgW = imgH * aspect;
                            } else {
                              imgW = shapeW;
                              imgH = imgW / aspect;
                            }
                            const scaleK = shapeScales[sIdx] || 1;
                            imgW *= scaleK;
                            imgH *= scaleK;
                            const leftBase = (shapeW - imgW) / 2;
                            const topBase = (shapeH - imgH) / 2;
                            const offsetX = shapeOffsets[sIdx] || 0;
                            const offsetY = shapeVerticalOffsets[sIdx] || 0;
                            return (
                              <>
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    dragStartRef.current = { x: e.clientX, y: e.clientY };
                                    setIsDragging(false);
                                    // Drag hanya diizinkan jika shape ini sedang dalam mode edit
                                    if (isEditing && (displayMode !== 'video' || !videoAssignments[sIdx])) {
                                      setPanState({ shapeIdx: sIdx, startClientX: e.clientX, startClientY: e.clientY, startOffset: offsetX, startVerticalOffset: offsetY });
                                    }
                                  }}
                                  style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    cursor: (displayMode === 'video' && videoAssignments[sIdx]) ? 'default' : 'grab',
                                    zIndex: 1,
                                  }}
                                >
                                  {displayMode === 'video' && videoAssignments[sIdx] ? (
                                    (() => {
                                      // Calculate original dimensions for video (without photo settings)
                                      const filename = selectedPhotos[assignedIdx];
                                      const dims = photoDims[filename] || { w: 1, h: 1 };
                                      const aspect = dims.w / Math.max(1, dims.h);
                                      const shapeAspect = shapeW / Math.max(1, shapeH);
                                      let videoW, videoH;
                                      if (aspect >= shapeAspect) {
                                        videoH = shapeH;
                                        videoW = videoH * aspect;
                                      } else {
                                        videoW = shapeW;
                                        videoH = videoW / aspect;
                                      }
                                      // Video always uses original dimensions (no scale, no offset)
                                      const videoLeftBase = (shapeW - videoW) / 2;
                                      const videoTopBase = (shapeH - videoH) / 2;

                                      return (
                                        <div style={{ position: 'relative' }}>
                                          <video
                                            src={getVideoUrl(sessionId, getPhotoNumberFromFilename(selectedPhotos[assignedIdx]))}
                                            alt={`video-${sIdx + 1}`}
                                            style={{
                                              position: 'absolute',
                                              left: videoLeftBase,
                                              top: videoTopBase,
                                              width: videoW,
                                              height: videoH,
                                              transform: 'scaleX(-1)', // Mirror the video horizontally
                                              objectFit: 'fill',
                                              userSelect: 'none',
                                              pointerEvents: 'none',
                                            }}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                          />
                                          {/* Video playing indicator */}
                                          <div className="video-playing-indicator">
                                            ▶ Playing
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <img
                                      src={assignedPhoto}
                                      alt={`ph-${sIdx + 1}`}
                                      style={{
                                        position: 'absolute',
                                        left: leftBase + offsetX,
                                        top: topBase + offsetY,
                                        width: imgW,
                                        height: imgH,
                                        transform: 'scaleX(-1)', // Mirror the photo horizontally
                                        objectFit: 'fill',
                                        userSelect: 'none',
                                        pointerEvents: 'none',
                                        filter: getCssFilter(selectedFilter), // <-- apply filter
                                      }}
                                      onTouchStart={(e) => handleTouchStart(e, sIdx)}
                                      onTouchMove={handleTouchMove}
                                      onTouchEnd={handleTouchEnd}
                                    />
                                  )}
                                </div>
                                {/* Overlay for action buttons, hanya tampil pada shape aktif */}
                                {isActive && (
                                  <div className="shape-action-overlay" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 100, pointerEvents: 'none' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const next = shapeAssignments.slice();
                                        next[sIdx] = null;
                                        setShapeAssignments(next);
                                        setShapeOffsets((prev) => prev.map((v, i) => (i === sIdx ? 0 : v)));
                                        setShapeVerticalOffsets((prev) => prev.map((v, i) => (i === sIdx ? 0 : v)));
                                        setShapeScales((prev) => prev.map((v, i) => (i === sIdx ? 1 : v)));
                                        const newVideoAssignments = { ...videoAssignments };
                                        delete newVideoAssignments[sIdx];
                                        setVideoAssignments(newVideoAssignments);
                                        setActiveShapeId(null);
                                        setEditingShapeId(null);
                                      }}
                                      title="Hapus foto dari shape"
                                      className="remove-photo-button"
                                      style={{ pointerEvents: 'auto' }}
                                    >
                                      ×
                                    </button>
                                      {/* Tombol checklist untuk menyelesaikan editing pada shape ini */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveShapeId(null);
                                          setEditingShapeId(null);
                                        }}
                                        title="Selesai mengatur foto"
                                        className="confirm-edit-button"
                                        style={{ pointerEvents: 'auto' }}
                                      >
                                        ✓
                                    </button>
                                    {(displayMode !== 'video' || !videoAssignments[sIdx]) && (
                                      <div className="zoom-controls" style={{ pointerEvents: 'auto' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShapeScales((prev) => {
                                              const newScales = prev.map((v, i) => (i === sIdx ? Math.max(1, Number((v - 0.1).toFixed(2))) : v));
                                              const newScale = newScales[sIdx];
                                              setTimeout(() => {
                                                const dims2 = photoDims[selectedPhotos[assignedIdx]] || { w: 1, h: 1 };
                                                const asp2 = dims2.w / Math.max(1, dims2.h);
                                                let iw, ih;
                                                if (asp2 >= shapeAspect) { ih = shapeH; iw = ih * asp2; } else { iw = shapeW; ih = iw / asp2; }
                                                iw *= newScale; ih *= newScale;
                                                const maxOffX = Math.max(0, (iw - shapeW) / 2);
                                                const maxOffY = Math.max(0, (ih - shapeH) / 2);
                                                setShapeOffsets((prev) => prev.map((v, i) => (i === sIdx ? Math.max(-maxOffX, Math.min(maxOffX, v)) : v)));
                                                setShapeVerticalOffsets((prev) => prev.map((v, i) => (i === sIdx ? Math.max(-maxOffY, Math.min(maxOffY, v)) : v)));
                                              }, 0);
                                              return newScales;
                                            });
                                          }}
                                          title="Zoom out"
                                          className="zoom-button"
                                        >
                                          −
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShapeScales((prev) => {
                                              const newScales = prev.map((v, i) => (i === sIdx ? Number((v + 0.1).toFixed(2)) : v));
                                              const newScale = newScales[sIdx];
                                              setTimeout(() => {
                                                const dims2 = photoDims[selectedPhotos[assignedIdx]] || { w: 1, h: 1 };
                                                const asp2 = dims2.w / Math.max(1, dims2.h);
                                                let iw, ih;
                                                if (asp2 >= shapeAspect) { ih = shapeH; iw = ih * asp2; } else { iw = shapeW; ih = iw / asp2; }
                                                iw *= newScale; ih *= newScale;
                                                const maxOffX = Math.max(0, (iw - shapeW) / 2);
                                                const maxOffY = Math.max(0, (ih - shapeH) / 2);
                                                setShapeOffsets((prev) => prev.map((v, i) => (i === sIdx ? Math.max(-maxOffX, Math.min(maxOffX, v)) : v)));
                                                setShapeVerticalOffsets((prev) => prev.map((v, i) => (i === sIdx ? Math.max(-maxOffY, Math.min(maxOffY, v)) : v)));
                                              }, 0);
                                              return newScales;
                                            });
                                          }}
                                          title="Zoom in"
                                          className="zoom-button"
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                    {assignedIdx != null && (
                                      <div className="photo-label" style={{ pointerEvents: 'none' }}>
                                        {displayMode === 'video' && videoAssignments[sIdx] ? `Video #${assignedIdx + 1}` : `Foto #${assignedIdx + 1}`}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <div className="no-photo-placeholder">No Photo</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Add video preview overlay when in video mode */}
                {displayMode === 'video' && (
                  <div className="video-mode-indicator">
                    🎥 Video Mode - 5s Preview
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#6c757d' }}>Tidak ada frame yang dipilih.</div>
            )}
            {/* Filter selector vertical bar */}
            <div className="filter-selector-vertical">
              {filterOptions.map(opt => (
                <button
                  key={opt.key}
                  className={`filter-circle-btn${selectedFilter === opt.key ? ' selected' : ''}`}
                  title={opt.label}
                  onClick={() => setSelectedFilter(opt.key)}
                >
                  <span style={{fontSize:'1.5rem'}}>{opt.icon}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden canvas for capturing frame */}
      <canvas ref={canvasRef} className="hidden-canvas" />
    </div>
  );
};

export default SelectedResults; 